import React from 'react';

import { getChannelReadiness } from '../../connectors/services/channelAccountService';
import type { ChannelAccountChannel, ChannelReadiness } from '../../connectors/types/channelAccount';
import type { ConversationChannelIconKind } from '../conversationChannelIcons';

/**
 * Connector setup changes rarely, and every opened conversation would otherwise refetch it, so the
 * request is shared process-wide and resolved once per session.
 */
let readinessPromise: Promise<ChannelReadiness> | null = null;

const loadReadiness = (): Promise<ChannelReadiness> => {
  if (!readinessPromise) {
    readinessPromise = getChannelReadiness().catch(error => {
      // Let a later conversation retry rather than caching the failure for the whole session.
      readinessPromise = null;
      throw error;
    });
  }

  return readinessPromise;
};

/** Exposed for tests, which would otherwise leak a resolved cache between cases. */
export const resetChannelReadinessCache = (): void => {
  readinessPromise = null;
};

/**
 * Maps the channel badge shown in the conversation header onto the connector that has to be set up
 * for it. Deriving both from the same icon kind keeps the warning from contradicting the badge.
 * Internal, AI and employee conversations need no connector, so they map to null.
 */
export const channelIconKindToAccountChannel = (
  kind: ConversationChannelIconKind | undefined,
): ChannelAccountChannel | null => {
  switch (kind) {
    case 'whatsApp':
      return 'WhatsApp';
    case 'instagram':
      return 'Instagram';
    case 'telegram':
      return 'Telegram';
    case 'messenger':
      return 'Messenger';
    case 'tiktok':
      return 'TikTok';
    case 'webchat':
      return 'Webchat';
    case 'phone':
      // Outbound to a bare phone number goes over the SMS gateway.
      return 'Sms';
    default:
      return null;
  }
};

/**
 * Resolves whether the given channel can currently carry a message. Returns null while unknown, so
 * callers can stay silent rather than warn on a channel that may well be configured.
 */
export const useChannelReadiness = (channel: ChannelAccountChannel | null): boolean | null => {
  const [readiness, setReadiness] = React.useState<ChannelReadiness | null>(null);

  React.useEffect(() => {
    if (!channel) {
      return;
    }

    let cancelled = false;
    loadReadiness()
      .then(result => {
        if (!cancelled) {
          setReadiness(result);
        }
      })
      .catch(() => {
        // A failed lookup must not imply "not configured"; stay silent instead.
        if (!cancelled) {
          setReadiness(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [channel]);

  if (!channel || !readiness) {
    return null;
  }

  return readiness[channel] ?? false;
};
